import { Form } from '@components/ui/Form';
import { useSignup } from '@hooks/useSignup';
import { GoogleIcon, AppleIcon } from '@hooks/auth/authSocialIcons';
import useThemeStore from '@stores/theme.store';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import Link from '@components/ui/Link';
import { Input } from '@components/ui/Input';
import {
    LoginRoot,
    LoginBrandLogo,
    LoginWelcome,
    LoginSubtitle,
    LoginSocialBtn,
    LoginOr,
    LoginFormWrapper,
    LoginSubmitButton,
    AuthHiddenAutofillTrap,
} from '../style';

const Signup = () => {
    const { form, loading, onFinish, handleGoogle, handleApple, isMac } = useSignup();
    const mode = useThemeStore((state) => state.mode);
    const authLogo = mode === 'dark' ? '/logo-dark.png' : '/logo.png';

    return (
        <LoginRoot>
            {/* <LoginBrandLogo src={authLogo} alt="Sprinto" /> */}
            <LoginWelcome>Create your account</LoginWelcome>
            <LoginSubtitle>
                Already have an account? <Link to="/sign-in">Sign in</Link>
            </LoginSubtitle>

            {isMac && (
                <LoginSocialBtn type="button" onClick={handleApple}>
                    <AppleIcon />
                    Continue with Apple
                </LoginSocialBtn>
            )}
            <LoginSocialBtn type="button" onClick={handleGoogle}>
                <GoogleIcon />
                Continue with Google
            </LoginSocialBtn>

            <LoginOr>or</LoginOr>

            <LoginFormWrapper>
                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    requiredMark={false}
                    autoComplete="off"
                    name="signup_form"
                    id="signup-form"
                >
                    <AuthHiddenAutofillTrap aria-hidden="true">
                        <input type="text" name="prevent_autofill_username" tabIndex={-1} autoComplete="off" readOnly />
                        <input
                            type="password"
                            name="prevent_autofill_password"
                            tabIndex={-1}
                            autoComplete="off"
                            readOnly
                        />
                    </AuthHiddenAutofillTrap>
                    <Form.Item name="signup_f1" rules={[{ required: true, message: 'Please enter your name' }]}>
                        <Input
                            noAutofill
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<UserOutlined />}
                            placeholder="Full name"
                        />
                    </Form.Item>
                    <Form.Item
                        name="signup_f2"
                        rules={[
                            { required: true, message: 'Please enter your email' },
                            { type: 'email', message: 'Invalid email' },
                        ]}
                    >
                        <Input
                            noAutofill
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<MailOutlined />}
                            placeholder="Email"
                        />
                    </Form.Item>
                    <Form.Item
                        name="signup_f3"
                        rules={[
                            { required: true, message: 'Please enter your phone number' },
                            {
                                validator: (_, value) =>
                                    !value || value.length >= 10
                                        ? Promise.resolve()
                                        : Promise.reject(
                                              new Error('Please enter a valid phone number (at least 10 digits)'),
                                          ),
                            },
                        ]}
                    >
                        <Input
                            noAutofill
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<PhoneOutlined />}
                            placeholder="+1234567890"
                        />
                    </Form.Item>
                    <Form.Item
                        name="signup_f4"
                        rules={[
                            { required: true, message: 'Please enter your password' },
                            {
                                validator: (_, value) =>
                                    !value || value.length >= 6
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('At least 6 characters')),
                            },
                        ]}
                    >
                        <Input
                            noAutofill
                            isPassword
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<LockOutlined />}
                            placeholder="Password"
                        />
                    </Form.Item>
                    <Form.Item
                        name="signup_f5"
                        dependencies={['signup_f4']}
                        rules={[
                            { required: true, message: 'Please confirm your password' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('signup_f4') === value) return Promise.resolve();
                                    return Promise.reject(new Error("Passwords don't match"));
                                },
                            }),
                        ]}
                    >
                        <Input
                            noAutofill
                            isPassword
                            $maxWidth="100%"
                            $background="transparent"
                            $size="0.9375rem"
                            $padding="12px 14px"
                            prefix={<LockOutlined />}
                            placeholder="Confirm password"
                        />
                    </Form.Item>
                    <Form.Item>
                        <LoginSubmitButton type="primary" htmlType="submit" block loading={loading} size="large">
                            Sign up
                        </LoginSubmitButton>
                    </Form.Item>
                </Form>
            </LoginFormWrapper>
        </LoginRoot>
    );
};

export default Signup;
